<script setup lang="ts">
import Tag from "~/components/Common/Tag.vue";
import { useFormats } from "~/composables/utils/useFormat";
import { ColorsTypeEnum } from "~/utils/enums/colores.enums";
import { DateChoiceStatusEnum } from "~/utils/enums/date-choice.enums";
import type { TrainingPanFindOneDetailsDateChoice } from "@simbelapp/order-sdk";

/**
 * "Choix de date" block of the registration details. It replaces the "Étapes" block, sits under the
 * summary and is present in every state of the "À planifier" tab (AC-SUIVI.5).
 */
const props = defineProps<{ dateChoice: TrainingPanFindOneDetailsDateChoice }>();

const formats = useFormats();

/** The block always starts with "Sollicité le". */
const rows = computed(() => {
  const { invitedAt, lastReminderAt, reminderCount, noSuitableDateAt, status } = props.dateChoice;

  const lines = [
    {
      key: "invited",
      label: "Sollicité le",
      value: invitedAt ? formats.toDateDisplay(invitedAt) : "—",
      hint: "",
    },
  ];

  // "Dernière relance · N relance(s)" stays visible even after a report (AC-ADN.4).
  if (reminderCount > 0 || status === DateChoiceStatusEnum.PENDING_DATE_CHOICE) {
    lines.push({
      key: "reminder",
      label: "Dernière relance",
      value: lastReminderAt ? formats.toDateDisplay(lastReminderAt) : "—",
      hint: `${reminderCount} relance${reminderCount > 1 ? "s" : ""}`,
    });
  }

  if (status === DateChoiceStatusEnum.NO_SUITABLE_DATE) {
    lines.push({
      key: "no_suitable_date",
      label: "Signalé « aucune date ne convient »",
      value: noSuitableDateAt ? formats.toDateDisplay(noSuitableDateAt) : "—",
      hint: "",
    });
  }

  return lines;
});
</script>

<template>
  <div class="pt-[8px] border-b border-stroke-color">
    <h3 class="heading_3_demi text-primary-color p-[8px]">Choix de date</h3>

    <div class="flex flex-col gap-[16px] p-[8px] mb-[32px]">
      <!-- Counter computed at render time, never frozen when the request was sent (AC-SUIVI.8) -->
      <Tag
        class="h-[26px]"
        :tag="{
          value: `${dateChoice.availableSessionsCount} session(s) disponible(s)`,
          type: ColorsTypeEnum.GREY,
          logo: 'date',
        }"
      />

      <div v-for="row in rows" :key="row.key" class="flex items-center gap-[12px]">
        <span class="body_regular text-secondary-color w-[150px] shrink-0">{{ row.label }}</span>
        <div class="flex items-center gap-[6px]">
          <span class="body_med text-primary-color">{{ row.value }}</span>
          <span v-if="row.hint" class="body_regular text-grey-medium-color">· {{ row.hint }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
